import { useState, useMemo, useEffect } from "react";
import {
  format,
  subDays,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  isWithinInterval,
  isAfter,
  parseISO,
  startOfDay,
  endOfDay,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const PRESETS = [
  { label: "3 dias", days: 3 },
  { label: "7 dias", days: 7 },
  { label: "30 dias", days: 30 },
] as const;

interface DateRangePickerProps {
  dateFrom: string;
  dateTo: string;
  onChange: (from: string, to: string) => void;
}

function toFromStr(d: Date): string {
  return format(startOfDay(d), "yyyy-MM-dd'T'HH:mm:ss");
}

function toToStr(d: Date): string {
  return format(endOfDay(d), "yyyy-MM-dd'T'HH:mm:ss");
}

function toDisplay(iso: string): string {
  return format(parseISO(iso), "dd/MM/yy", { locale: ptBR });
}

export function DateRangePicker({
  dateFrom,
  dateTo,
  onChange,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState<Date>(() =>
    dateFrom ? startOfMonth(parseISO(dateFrom)) : startOfMonth(new Date()),
  );
  const [pendingFrom, setPendingFrom] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | null>(null);
  const [activePreset, setActivePreset] = useState<number | null>(7);

  useEffect(() => {
    if (!dateFrom && !dateTo) setActivePreset(null);
  }, [dateFrom, dateTo]);

  const fromDate = dateFrom ? parseISO(dateFrom) : null;
  const toDate = dateTo ? parseISO(dateTo) : null;

  // Normalize range endpoints — always min→max, supports hover preview during selection
  function getRange(): { from: Date | null; to: Date | null } {
    if (pendingFrom) {
      const end = hovered ?? pendingFrom;
      return isAfter(pendingFrom, end)
        ? { from: end, to: pendingFrom }
        : { from: pendingFrom, to: end };
    }
    return { from: fromDate, to: toDate };
  }

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [viewDate]);

  function handleDayClick(day: Date) {
    setActivePreset(null);
    if (!pendingFrom) {
      setPendingFrom(day);
      onChange(toFromStr(day), "");
    } else {
      const end = hovered ?? day;
      const [a, b] = isAfter(pendingFrom, end)
        ? [end, pendingFrom]
        : [pendingFrom, end];
      onChange(toFromStr(a), toToStr(b));
      setPendingFrom(null);
      setHovered(null);
    }
  }

  function handlePreset(days: number) {
    const to = new Date();
    const from = subDays(to, days - 1);
    setActivePreset(days);
    setPendingFrom(null);
    setHovered(null);
    onChange(toFromStr(from), toToStr(to));
  }

  const dateLabel =
    dateFrom && dateTo
      ? `${toDisplay(dateFrom)} - ${toDisplay(dateTo)}`
      : dateFrom
        ? toDisplay(dateFrom)
        : "Selecionar período";

  const { from: rangeFrom, to: rangeTo } = getRange();

  return (
    <div className="flex w-full min-w-[400px] flex-col gap-1 rounded-[99px] border bg-card p-1 md:flex-row md:items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="h-8 w-full justify-start gap-2 rounded-[99px] px-3 text-left md:flex-1"
          >
            <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate whitespace-nowrap text-sm font-medium">
              {dateLabel}
            </span>
          </Button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-auto p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewDate((d) => subMonths(d, 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm font-medium capitalize">
              {format(viewDate, "MMMM yyyy", { locale: ptBR })}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 mb-1">
            {WEEKDAYS.map((d) => (
              <div
                key={d}
                className="h-8 w-8 flex items-center justify-center text-[11px] text-muted-foreground font-medium"
              >
                {d}
              </div>
            ))}
          </div>

          {/* Day grid */}
          <div
            className="grid grid-cols-7"
            onMouseLeave={() => pendingFrom && setHovered(null)}
          >
            {days.map((day, i) => {
              const outside = !isSameMonth(day, viewDate);
              const isStart = rangeFrom ? isSameDay(day, rangeFrom) : false;
              const isEnd = rangeTo ? isSameDay(day, rangeTo) : false;
              const inRange =
                rangeFrom && rangeTo
                  ? isWithinInterval(day, { start: rangeFrom, end: rangeTo }) &&
                    !isStart &&
                    !isEnd
                  : false;
              const isToday = isSameDay(day, new Date());

              return (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => pendingFrom && setHovered(day)}
                  onClick={() => handleDayClick(day)}
                  className={cn(
                    "h-8 w-8 mx-auto flex items-center justify-center text-xs transition-colors",
                    outside && "text-muted-foreground opacity-40",
                    isToday && !isStart && !isEnd && "font-bold",
                    inRange && "bg-accent",
                    (isStart || isEnd) &&
                      "bg-primary text-primary-foreground rounded-full font-semibold",
                    !isStart &&
                      !isEnd &&
                      !inRange &&
                      !outside &&
                      "hover:bg-accent hover:rounded-full",
                  )}
                >
                  {format(day, "d")}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {PRESETS.map(({ label, days }) => (
        <button
          key={days}
          type="button"
          onClick={() => handlePreset(days)}
          className={cn(
            "h-8 min-w-[64px] flex-1 flex items-center justify-center rounded-[99px] px-3 text-xs font-bold transition-colors md:flex-none",
            activePreset === days
              ? "bg-foreground text-background"
              : "text-muted-foreground hover:bg-muted hover:text-foreground",
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
